
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

interface InvitationDetails {
  email: string;
  role: 'user' | 'admin';
  expires_at: string;
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    if (!token) {
      toast({
        variant: "destructive",
        title: "Ugyldig invitasjon",
        description: "Invitasjonstoken mangler."
      });
      navigate('/auth');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('validate_invitation_token', { token });
      
      if (error || !data || data.length === 0) {
        toast({
          variant: "destructive",
          title: "Ugyldig invitasjon",
          description: "Invitasjonen er utløpt eller ugyldig."
        });
        navigate('/auth');
        return;
      }

      setInvitation(data[0]);
    } catch (error: any) {
      console.error('Validation error:', error);
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Kunne ikke validere invitasjonen."
      });
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passordmismatch",
        description: "Passordene må være like."
      });
      return;
    }

    if (password.length < 6) {
      toast({
        variant: "destructive",
        title: "Kort passord",
        description: "Passordet må være minst 6 tegn."
      });
      return;
    }

    setSubmitting(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: invitation!.email,
        password,
        options: {
          data: {
            full_name: fullName,
            invitation_token: token
          }
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Konto opprettet!",
        description: "Du er nå registrert. Bekreft epost-adressen din for å logge inn."
      });

      navigate('/');

    } catch (error: any) {
      console.error('Registration error:', error);
      toast({
        variant: "destructive",
        title: "Registreringsfeil",
        description: error.message || "Kunne ikke opprette konto."
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4">
            <h1 className="text-4xl font-bold text-blue-600">Klara<span className="text-blue-700">.ai</span></h1>
          </div>
          <CardTitle className="text-2xl">Opprett konto</CardTitle>
          <CardDescription>
            Du er invitert til å bli {invitation.role === 'admin' ? 'administrator' : 'bruker'} i Klara.ai
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={invitation.email}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Fullt navn</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Ditt fulle navn"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Velg et sikkert passord"
                required
                minLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekreft passord</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Skriv passordet på nytt"
                required
                minLength={6}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Oppretter konto...
                </>
              ) : (
                'Opprett konto'
              )}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-blue-800">
              <CheckCircle className="h-4 w-4" />
              <span>Rolle: {invitation.role === 'admin' ? 'Administrator' : 'Bruker'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-800 mt-1">
              <AlertCircle className="h-4 w-4" />
              <span>Utløper: {new Date(invitation.expires_at).toLocaleDateString('nb-NO')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
